import {
  AnalyseRequest,
  AnalyseResponse,
  GenerateEmailRequest,
  GeneratedEmailResponse,
  ProgressStepKey,
  ProgressStepStatus,
} from "../types";
import { apiFetch } from "../office/apiClient";

type StepCallback = (
  key: ProgressStepKey,
  status: ProgressStepStatus,
  durationSec?: number,
) => void;

interface StreamLine<TRes> {
  type: "step" | "result";
  key?: ProgressStepKey;
  status?: ProgressStepStatus;
  durationSec?: number;
  payload?: TRes;
}

// Shared NDJSON reader for the streaming AI endpoints (/api/analyse/,
// /api/generate-email/). POSTs through apiFetch (so the auth header is always
// attached), drives progress via onStep as "step" events arrive, and returns the
// final "result" payload. Generic over the result shape so each endpoint stays typed.
async function streamRequest<TRes>(
  path: string,
  req: unknown,
  onStep: StepCallback,
  signal: AbortSignal,
): Promise<TRes> {
  const res = await apiFetch(path, {
    method: "POST",
    body: JSON.stringify(req),
    signal,
  });

  if (!res.ok) {
    // Surface the backend's explanation (e.g. a 422 validation error names the
    // exact field), instead of a bare status code — makes schema mismatches
    // between a stale backend and the add-in self-diagnosing.
    let detail = "";
    try {
      const body = await res.text();
      try {
        const parsed = JSON.parse(body);
        detail = typeof parsed?.detail === "string" ? parsed.detail : JSON.stringify(parsed?.detail ?? parsed);
      } catch {
        detail = body;
      }
    } catch {
      /* body unavailable */
    }
    const suffix = detail ? ` — ${detail.slice(0, 300)}` : "";
    throw new Error(`Request failed: ${res.status} ${res.statusText}${suffix}`);
  }
  if (!res.body) {
    throw new Error("Response has no body");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let result: TRes | null = null;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let nlIdx: number;
    while ((nlIdx = buffer.indexOf("\n")) !== -1) {
      const rawLine = buffer.slice(0, nlIdx).trim();
      buffer = buffer.slice(nlIdx + 1);
      if (!rawLine) continue;

      let line: StreamLine<TRes>;
      try {
        line = JSON.parse(rawLine);
      } catch {
        continue;
      }

      if (line.type === "step" && line.key && line.status) {
        onStep(line.key, line.status, line.durationSec);
      } else if (line.type === "result" && line.payload) {
        result = line.payload;
      }
    }
  }

  if (!result) {
    throw new Error("Stream ended without a result event");
  }
  return result;
}

export function streamAnalyse(
  path: string,
  req: AnalyseRequest,
  onStep: StepCallback,
  signal: AbortSignal,
): Promise<AnalyseResponse> {
  return streamRequest<AnalyseResponse>(path, req, onStep, signal);
}

export function streamGenerate(
  path: string,
  req: GenerateEmailRequest,
  onStep: StepCallback,
  signal: AbortSignal,
): Promise<GeneratedEmailResponse> {
  return streamRequest<GeneratedEmailResponse>(path, req, onStep, signal);
}
